class Logger {
  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.isTest = process.env.NODE_ENV === 'test';
  }

  /**
   * @param {any} error - Error to extract details from
   * @returns {Object} Extracted error details
   */
  extractErrorDetails(error) {
    if (error instanceof Error) {
      return {
        name: error.name,
        message: error.message,
        stack: this.isDevelopment ? error.stack : undefined,
        cause: error.cause
      };
    }

    if (typeof error === 'object' && error !== null) {
      const details = {};
      
      if (error.message) details.message = error.message;
      if (error.name) details.name = error.name;
      if (error.code) details.code = error.code;
      if (error.status) details.status = error.status;
      if (error.statusText) details.statusText = error.statusText;
      if (error.response) details.response = this.extractResponseDetails(error.response);
      if (this.isDevelopment && error.stack) details.stack = error.stack;
      
      if (Object.keys(details).length === 0) {
        return { originalError: error, type: typeof error };
      }
      
      return details;
    }

    return { value: error, type: typeof error };
  }

  /**
   * Extract response details from HTTP responses
   * @param {any} response - Response object
   * @returns {Object} Extracted response details
   */
  extractResponseDetails(response) {
    if (typeof response === 'object' && response !== null) {
      return {
        status: response.status,
        statusText: response.statusText,
        data: response.data,
        headers: this.isDevelopment ? response.headers : undefined
      };
    }
    return response;
  }

  /**
   * Sanitize data to remove sensitive information
   * @param {any} data - Data to sanitize
   * @returns {any} Sanitized data
   */
  sanitize(data) {
    if (typeof data === 'string') {
      return data.replace(/Bearer\s+[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+/g, 'Bearer [TOKEN]');
    }

    if (typeof data === 'object' && data !== null) {
      const sanitized = Array.isArray(data) ? [] : {};
      
      for (const [key, value] of Object.entries(data)) {
        if (['token', 'accessToken', 'refreshToken', 'password', 'authorization'].includes(key.toLowerCase())) {
          sanitized[key] = '[REDACTED]';
        } else if (typeof value === 'object' && value !== null) {
          sanitized[key] = this.sanitize(value);
        } else {
          sanitized[key] = value;
        }
      }
      
      return sanitized;
    }

    return data;
  }

  /**
   * Log debug information (development only)
   * @param {string} message - Log message
   * @param {any} data - Additional data to log
   */
  debug(message, data = null) {
    if (this.isDevelopment && !this.isTest) {
      const sanitizedData = data ? this.sanitize(data) : null;
      if (sanitizedData) {
        console.log(`[DEBUG] ${message}`, sanitizedData);
      } else {
        console.log(`[DEBUG] ${message}`);
      }
    }
  }

  /**
   * Log information (development only)
   * @param {string} message - Log message
   * @param {any} data - Additional data to log
   */
  info(message, data = null) {
    if (this.isDevelopment && !this.isTest) {
      const sanitizedData = data ? this.sanitize(data) : null;
      if (sanitizedData) {
        console.info(`[INFO] ${message}`, sanitizedData);
      } else {
        console.info(`[INFO] ${message}`);
      }
    }
  }

  /**
   * Log warnings (development only)
   * @param {string} message - Warning message
   * @param {any} data - Additional data to log
   */
  warn(message, data = null) {
    if (this.isDevelopment && !this.isTest) {
      const sanitizedData = data ? this.sanitize(data) : null;
      if (sanitizedData) {
        console.warn(`[WARN] ${message}`, sanitizedData);
      } else {
        console.warn(`[WARN] ${message}`);
      }
    }
  }

  /**
   * Log errors (all environments)
   * @param {string} message - Error message
   * @param {Error|any} error - Error object or additional data
   */
  error(message, error = null) {
    if (error) {
      const errorDetails = this.extractErrorDetails(error);
      const sanitizedError = this.sanitize(errorDetails);
      console.error(`[ERROR] ${message}`, sanitizedError);
    } else {
      console.error(`[ERROR] ${message}`);
    }
  }

  /**
   * Log API request information (development only)
   * @param {string} method - HTTP method
   * @param {string} url - Request URL
   * @param {number} duration - Request duration in ms
   * @param {boolean} hasToken - Whether request had auth token
   */
  apiRequest(method, url, duration, hasToken = false) {
    if (this.isDevelopment && !this.isTest) {
      console.log(`[API] ${method.toUpperCase()} ${url} - ${duration}ms ${hasToken ? '🔐' : '🔓'}`);
    }
  }

  /**
   * Log authentication events (development only)
   * @param {string} event - Authentication event
   * @param {Object} details - Event details (will be sanitized)
   */
  auth(event, details = null) {
    if (this.isDevelopment && !this.isTest) {
      const sanitizedDetails = details ? this.sanitize(details) : null;
      if (sanitizedDetails) {
        console.log(`[AUTH] ${event}`, sanitizedDetails);
      } else {
        console.log(`[AUTH] ${event}`);
      }
    }
  }

  /**
   * Log performance metrics (development only)
   * @param {string} operation - Operation name
   * @param {number} duration - Duration in ms
   * @param {any} metadata - Additional metadata
   */
  performance(operation, duration, metadata = null) {
    if (this.isDevelopment && !this.isTest) {
      const sanitizedMetadata = metadata ? this.sanitize(metadata) : null;
      const message = `[PERF] ${operation} - ${duration}ms`;
      
      if (sanitizedMetadata) {
        console.log(message, sanitizedMetadata);
      } else {
        console.log(message);
      }
    }
  }
}

const logger = new Logger();

export default logger;

export const { debug, info, warn, error, apiRequest, auth, performance } = logger;